import { Agenda } from '../types';

export interface SlotAvailabilityCheck {
  isAvailable: boolean;
  isDisabled: boolean;
  reason?: string;
  statusText: string;
}

/**
 * Faz parse do campo `horainicio` vindo do legado, garantindo consistência de timezone/formato.
 * Suporta:
 * - ISO (ex.: 2026-03-27T17:20:00-03:00)
 * - "YYYY-MM-DD HH:mm:ss" (interpreta como horário local)
 * - "YYYY-MM-DD" (meia-noite local)
 * - "HH:mm" / "HH:mm:ss" (combina com a data de referência, quando fornecida)
 */
export function parseSlotStartDateTime(horainicio: string, baseDate?: Date): Date | null {
  const raw = (horainicio || '').trim();
  if (!raw) return null;

  const isIso = /^\d{4}-\d{2}-\d{2}T/.test(raw);
  const isDateTimeSpace = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}(:\d{2})?/.test(raw);
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
  const isTimeOnly = /^\d{2}:\d{2}(:\d{2})?$/.test(raw);

  let date: Date;

  if (isIso) {
    date = new Date(raw);
  } else if (isDateTimeSpace) {
    date = new Date(raw.replace(' ', 'T'));
  } else if (isDateOnly) {
    date = new Date(`${raw}T00:00:00`);
  } else if (isTimeOnly) {
    const ref = baseDate ? new Date(baseDate) : new Date();
    const yyyy = ref.getFullYear();
    const mm = String(ref.getMonth() + 1).padStart(2, '0');
    const dd = String(ref.getDate()).padStart(2, '0');
    const time = raw.length === 5 ? `${raw}:00` : raw;
    date = new Date(`${yyyy}-${mm}-${dd}T${time}`);
  } else {
    date = new Date(raw);
  }

  return Number.isFinite(date.getTime()) ? date : null;
}

/**
 * Formata o horário do slot para exibição.
 */
export function formatSlotTime(slot: Agenda, baseDate?: Date, locale: string = 'pt-BR'): string {
  const slotDate = parseSlotStartDateTime(slot.horainicio, baseDate);
  if (!slotDate) {
    const match = (slot.horainicio || '').match(/(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : '';
  }
  return slotDate.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Verifica a disponibilidade de um slot baseado nas condições do sistema legado
 * Implementa as mesmas regras do buildSelectEvents() do JS original
 */
export function checkSlotAvailability(slot: Agenda, t?: (key: string) => string, baseDate?: Date): SlotAvailabilityCheck {
  // {|INICIO| 28/03/2026 às 00:20 - AGENDA: PADRONIZAR PARSE DE DATA/HORA E MOTIVOS DE INDISPONIBILIDADE}
  const now = new Date();

  const slotDate = parseSlotStartDateTime(slot.horainicio, baseDate);
  const isSameDayAsNow = !!slotDate && (
    slotDate.getFullYear() === now.getFullYear() &&
    slotDate.getMonth() === now.getMonth() &&
    slotDate.getDate() === now.getDate()
  );

  // Versão anterior (histórico): comparação por hora/minuto e parse direto de Date(slot.horainicio)
  /*
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const legacySlotDate = new Date(slot.horainicio);
  const slotHour = legacySlotDate.getHours();
  const slotMinute = legacySlotDate.getMinutes();
  const today = new Date().toDateString();
  const slotDateString = legacySlotDate.toDateString();
  const isToday = today === slotDateString;
  const isPastTimeLegacy = isToday && (
    currentHour > slotHour ||
    (currentHour === slotHour && currentMinute >= slotMinute)
  );
  */

  const isPastTime = !!slotDate && isSameDayAsNow && now.getTime() >= slotDate.getTime();

  const hasNoVacancies = slot.vagas <= 0;
  /* {|INICIO| 24/06/2026 às 09:44 - BLOQUEAR NOVA RESERVA QUANDO O LIMITE POR CPF JA FOI CONSUMIDO} */
  const quantidadeMaximaPorCpfDisponivel = Number(slot.quantidade_maxima_por_cpf_disponivel);
  const hasReachedCpfLimit = Number.isFinite(quantidadeMaximaPorCpfDisponivel) && quantidadeMaximaPorCpfDisponivel <= 0;
  /* {|FIM| 24/06/2026 às 09:44 - BLOQUEAR NOVA RESERVA QUANDO O LIMITE POR CPF JA FOI CONSUMIDO} */
  const isClosed = Boolean(slot.fechado);
  
  // Determina o status do slot
  let isDisabled = false;
  let isAvailable = true;
  let reason = '';
  let statusText = '';
  
  // Prioridade de motivo:
  // 1) Horário já passou (mesmo que sem vagas/fechado) para manter coerência visual
  // 2) Fechado
  // 3) Sem vagas
  if (isPastTime) {
    isDisabled = true;
    isAvailable = false;
    reason = t ? t('calendar.timeExpired') : 'Horário já passou';
    statusText = 'INDISPONÍVEL';
  } else if (isClosed) {
    isDisabled = true;
    isAvailable = false;
    reason = t ? t('calendar.closed') : 'Bateria fechada';
    statusText = 'INDISPONÍVEL';
  } else if (hasReachedCpfLimit) {
    isDisabled = true;
    isAvailable = false;
    reason = t ? t('calendar.cpfLimitReached') : 'Limite por CPF atingido';
    statusText = 'INDISPONÍVEL';
  } else if (hasNoVacancies) {
    isDisabled = true;
    isAvailable = false;
    reason = t ? t('calendar.noVacancies') : 'Sem vagas disponíveis';
    statusText = 'INDISPONÍVEL';
  } else {
    // Slot disponível
    // Fallback esperto: tenta app.slots, senão reservations.slots, senão padrão
    const appSlots = t ? t('app.slots') : undefined;
    const reservationsSlots = t ? t('reservations.slots') : undefined;
    const slotsLabel = appSlots && appSlots !== 'app.slots'
      ? appSlots
      : reservationsSlots && reservationsSlots !== 'reservations.slots'
        ? reservationsSlots
        : 'VAGAS';
    statusText = `${slot.vagas} ${slotsLabel}`;
  }
  
  // {|FIM| 28/03/2026 às 00:20 - AGENDA: PADRONIZAR PARSE DE DATA/HORA E MOTIVOS DE INDISPONIBILIDADE}
  return {
    isAvailable,
    isDisabled,
    reason,
    statusText
  };
}

/**
 * Formata o texto de status das vagas (baseado no JS legado)
 */
export function formatVacancyText(slot: Agenda): string {
  const availability = checkSlotAvailability(slot);
  return availability.statusText;
}

/**
 * Verifica se o usuário deve ver o botão de login (quando não logado)
 */
export function shouldShowLoginPrompt(isLoggedIn: boolean): boolean {
  return !isLoggedIn;
}

/**
 * Aplica classes CSS baseadas na disponibilidade (similar ao JS legado)
 */
export function getSlotCssClasses(slot: Agenda, selectedDate: Date, isLoggedIn: boolean): string[] {
  const classes: string[] = ['event-container', 'slide-top'];
  const availability = checkSlotAvailability(slot, undefined, selectedDate);
  
  // Se não está logado, desabilita todos os slots
  if (!isLoggedIn) {
    classes.push('disable-div');
  }
  
  // Se o slot está indisponível por qualquer motivo, adiciona classe de desabilitado
  if (availability.isDisabled) {
    classes.push('disable-div');
  }
  
  return classes;
}

/**
 * Verifica se a opção "Bateria Fechada" deve estar desabilitada.
 * Regras (legado):
 * - Indisponível quando (vagas !== vagas_totais) OU (fechado == 1).
 * - Se requireExtras=true e não houver dados confiáveis de vagas_totais/fechado, desabilita por segurança.
 *
 * Comentário (function-level): Centraliza a regra para reutilização no modal e testes unitários.
 */
export function isClosedReservationDisabled(
  slot: { vagas?: number; vagas_totais?: number; fechado?: boolean; permite_somente_quantidade?: number } | Agenda,
  extras?: { vagas_totais?: number; fechado?: boolean; permite_somente_quantidade?: number } | null,
  requireExtras: boolean = true
): boolean {
  const rawTotal = (extras?.vagas_totais ?? (slot as unknown as { vagas_totais?: number })?.vagas_totais);
  /* {|INICIO| 18/06/2026 às 16:39 - DESABILITAR BATERIA FECHADA QUANDO HOUVER LIMITE EXCLUSIVO DE QUANTIDADE} */
  const limiteExclusivoQuantidade = Number(
    extras?.permite_somente_quantidade ??
    (slot as unknown as { permite_somente_quantidade?: number })?.permite_somente_quantidade ??
    0
  );
  /* {|FIM| 18/06/2026 às 16:39 - DESABILITAR BATERIA FECHADA QUANDO HOUVER LIMITE EXCLUSIVO DE QUANTIDADE} */
  const hasExtrasData = (
    typeof rawTotal === 'number' ||
    typeof (slot as unknown as { vagas_totais?: number })?.vagas_totais === 'number' ||
    typeof extras?.fechado === 'boolean' ||
    typeof (slot as unknown as { fechado?: boolean })?.fechado === 'boolean'
  );
  if (requireExtras && !hasExtrasData) {
    return true;
  }
  const vagasTotaisCalc = (typeof rawTotal === 'number' && Number.isFinite(rawTotal) && rawTotal > 0)
    ? rawTotal
    : (Number((slot as unknown as { vagas?: number })?.vagas) || 0);
  const isFechadoCalc = (typeof extras?.fechado === 'boolean')
    ? Boolean(extras?.fechado)
    : Boolean((slot as unknown as { fechado?: boolean })?.fechado);
  return (
    ((Number((slot as unknown as { vagas?: number })?.vagas) || 0) !== vagasTotaisCalc) ||
    isFechadoCalc ||
    (Number.isFinite(limiteExclusivoQuantidade) && limiteExclusivoQuantidade > 0)
  );
}
